import { useLiveQuery } from '@electric-sql/pglite-react';
import { Button } from '@quickreport/ui';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import clsx from 'clsx';
import {
  Bell,
  CalendarDays,
  FileText,
  Info,
  LayoutDashboard,
  LayoutTemplate,
  Lock as LockIcon,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Receipt,
  SlidersHorizontal,
  Store,
  UserRound,
  Users,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useSyncStatus } from '../auth/AuthProvider';
import { usePendingBookings } from '../lib/bookings';
import { NotificationsProvider, useNotifications } from '../lib/notifications';
import { useInReportEditor } from '../lib/use-in-editor';
import { useWorkspace } from '../lib/workspace';
import { PlanBanner } from './PlanBanner';
import { StatusBanners } from './StatusBanners';

interface NavItem {
  to: string;
  label: string;
  icon: typeof FileText;
  /** Shown only to people who may do this. */
  need?: Parameters<ReturnType<typeof useWorkspace>['can']>[0];
  /** A count beside the name, such as unread notifications. */
  badge?: 'notifications' | 'bookings';
}

const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, need: 'view_dashboard' },
  { to: '/notifications', label: 'Notifications', icon: Bell, badge: 'notifications' },
  { to: '/reports', label: 'Reports', icon: FileText },
  { to: '/patients', label: 'Patients', icon: UserRound },
  { to: '/templates', label: 'Templates', icon: LayoutTemplate },
  {
    to: '/bookings',
    label: 'Bookings',
    icon: CalendarDays,
    need: 'register_visit',
    badge: 'bookings',
  },
  { to: '/expenses', label: 'Expenses', icon: Receipt, need: 'manage_expenses' },
  { to: '/settings', label: 'Report Settings', icon: SlidersHorizontal },
  { to: '/public-page', label: 'Public page', icon: Store, need: 'manage_site' },
  { to: '/team', label: 'Account & Team', icon: Users },
  { to: '/about', label: 'About', icon: Info },
];

/** A count in a small round badge; a dot-sized one when the menu is folded. */
function CountBadge({ n, compact }: { n: number; compact: boolean }) {
  if (n <= 0) return null;
  return (
    <span
      className={clsx(
        'grid place-items-center rounded-full bg-accent font-semibold text-white',
        compact
          ? 'absolute top-1 right-1 h-4 min-w-4 px-1 text-[10px]'
          : 'ml-auto h-5 min-w-5 px-1.5 text-[11px]',
      )}
    >
      <span aria-hidden>{n > 99 ? '99+' : n}</span>
      <span className="sr-only">{n} unread</span>
    </span>
  );
}

function NavLinks({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const ws = useWorkspace();
  const notes = useNotifications();
  const bookings = usePendingBookings(ws.can('register_visit'));
  return (
    <nav className="grid gap-0.5 px-2 py-1" aria-label="Main">
      {NAV.filter((n) => !n.need || ws.can(n.need)).map(({ to, label, icon: Icon, badge }) => (
        <Link
          key={to}
          to={to}
          onClick={onNavigate}
          title={collapsed ? label : undefined}
          aria-label={collapsed ? label : undefined}
          className={clsx(
            'relative flex h-10 items-center rounded-lg border-l-[3px] border-transparent text-slate transition-colors hover:bg-canvas hover:text-navy',
            collapsed ? 'justify-center' : 'gap-2.5 px-3',
          )}
          activeProps={{
            className: 'bg-brand-soft !border-accent !text-navy font-semibold',
          }}
        >
          <Icon size={18} strokeWidth={1.8} />
          {collapsed ? null : label}
          {badge === 'notifications' ? <CountBadge n={notes.unread} compact={collapsed} /> : null}
          {badge === 'bookings' ? <CountBadge n={bookings.pending} compact={collapsed} /> : null}
        </Link>
      ))}
    </nav>
  );
}

/** Everything inside the menu: logo, lab, links, sync, who is signed in. Used by the side menu and the phone drawer. */
function SidebarBody({
  collapsed,
  onToggle,
  setLeaving,
  onNavigate,
}: {
  collapsed: boolean;
  onToggle?: () => void;
  setLeaving: (n: number | null) => void;
  onNavigate?: () => void;
}) {
  const ws = useWorkspace();
  const { signOut, lock, pinSet } = useAuth();
  return (
    <>
      <div className={clsx('flex h-14 items-center', collapsed ? 'justify-center' : 'gap-2 px-4')}>
        <img src="/labview-mark.svg" alt={collapsed ? 'LabView' : ''} className="h-8 w-8" />
        {collapsed ? null : (
          <span className="min-w-0 flex-1 leading-tight">
            <span className="block text-[15px] font-semibold tracking-tight">LabView</span>
            <a
              href="https://aharbal.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-[11px] text-slate hover:text-navy hover:underline"
            >
              by Aharbal
            </a>
          </span>
        )}
        {collapsed || !onToggle ? null : (
          <button
            type="button"
            onClick={onToggle}
            aria-label="Collapse the menu"
            title="Collapse the menu"
            className="grid h-8 w-8 place-items-center rounded-lg text-slate hover:bg-canvas hover:text-navy"
          >
            <PanelLeftClose size={17} />
          </button>
        )}
      </div>
      {collapsed && onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          aria-label="Expand the menu"
          title="Expand the menu"
          className="mx-auto mb-2 grid h-9 w-9 place-items-center rounded-lg text-slate hover:bg-canvas hover:text-navy"
        >
          <PanelLeftOpen size={18} />
        </button>
      ) : (
        <div className="mx-3 mb-3 rounded-xl border border-line bg-canvas px-3 py-2">
          <div className="text-[10px] font-semibold tracking-wider text-slate uppercase">Lab</div>
          <div className="truncate text-[13px] font-semibold text-navy">{ws.auth.orgName}</div>
        </div>
      )}
      <NavLinks collapsed={collapsed} {...(onNavigate ? { onNavigate } : {})} />
      <div className="mt-auto flex min-w-0 flex-col gap-3 border-t border-line p-3">
        <SyncIndicator compact={collapsed} />
        <div
          className={clsx(
            'flex items-center rounded-xl bg-canvas',
            collapsed ? 'justify-center p-1.5' : 'gap-2.5 p-2',
          )}
          title={collapsed ? ws.auth.userName || ws.auth.email || '' : undefined}
        >
          <span
            aria-hidden
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-navy text-[13px] font-semibold text-white"
          >
            {(ws.auth.userName || ws.auth.email || '?').trim().charAt(0).toUpperCase()}
          </span>
          {collapsed ? null : (
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13px] font-semibold">{ws.auth.userName || 'You'}</div>
              <div className="truncate text-[11px] text-slate">
                {ws.auth.email ?? ws.auth.phone}
              </div>
            </div>
          )}
        </div>
        <div className={clsx('flex gap-1.5', collapsed && 'flex-col items-center')}>
          {pinSet ? (
            <button
              type="button"
              aria-label="Lock"
              title="Lock (open again with your PIN)"
              onClick={() => void lock()}
              className={clsx(
                'flex h-8 items-center justify-center gap-1.5 rounded-lg border border-navy/20 text-[13px] font-medium text-navy hover:bg-brand-soft',
                collapsed ? 'w-9' : 'flex-1',
              )}
            >
              <LockIcon size={14} /> {collapsed ? null : 'Lock'}
            </button>
          ) : null}
          <button
            type="button"
            aria-label="Sign out"
            title="Sign out"
            onClick={async () => {
              const r = await signOut(false);
              if (r) setLeaving(r.blocked);
            }}
            className={clsx(
              'flex h-8 items-center justify-center gap-1.5 rounded-lg border border-line text-[13px] font-medium text-slate hover:bg-canvas hover:text-navy',
              collapsed ? 'w-9' : 'flex-1',
            )}
          >
            <LogOut size={14} /> {collapsed ? null : 'Sign out'}
          </button>
        </div>
      </div>
    </>
  );
}

export function AppShell() {
  const ws = useWorkspace();
  const { signOut, lock, pinSet } = useAuth();
  const [leaving, setLeaving] = useState<number | null>(null);
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const narrow = window.innerWidth < 1024;
    try {
      const saved = localStorage.getItem('qr.nav.collapsed');
      return saved === null ? narrow : saved === '1';
    } catch {
      return narrow;
    }
  });
  const toggle = () =>
    setCollapsed((c) => {
      try {
        localStorage.setItem('qr.nav.collapsed', c ? '0' : '1');
      } catch {
        // storage blocked: it simply opens again next time
      }
      return !c;
    });

  return (
    <NotificationsProvider>
      <div className="flex h-full flex-col md:flex-row">
        <MobileBar onMenu={() => setDrawer(true)} />
        <aside
          className={clsx(
            'hidden shrink-0 flex-col border-r border-line bg-paper transition-[width] duration-150 md:flex',
            collapsed ? 'w-[4.5rem]' : 'w-60',
          )}
          aria-label="Menu"
        >
          <SidebarBody collapsed={collapsed} onToggle={toggle} setLeaving={setLeaving} />
        </aside>
        <main className="min-w-0 flex-1 overflow-auto pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0">
          {ws.auth.support ? (
            <div
              role="status"
              className="sticky top-0 z-30 flex flex-wrap items-center gap-x-3 gap-y-1 bg-navy px-4 py-2 text-[13px] text-white"
            >
              <span className="min-w-0 flex-1">
                <b>LabView support:</b> {ws.auth.support.by} is signed in as {ws.auth.userName}.
                Everything done here is real and is recorded.
              </span>
              <button
                type="button"
                className="rounded-md border border-white/40 px-2.5 py-1 font-medium hover:bg-white/10"
                onClick={async () => {
                  const r = await signOut(false);
                  if (r) setLeaving(r.blocked);
                }}
              >
                End support session
              </button>
            </div>
          ) : null}
          <StatusBanners />
          <ConflictBanner />
          <DueBanner />
          <PlanBanner />
          <Outlet />
        </main>
        <MobileTabs onMenu={() => setDrawer(true)} />
        {drawer ? (
          <Drawer onClose={() => setDrawer(false)}>
            <SidebarBody
              collapsed={false}
              setLeaving={setLeaving}
              onNavigate={() => setDrawer(false)}
            />
          </Drawer>
        ) : null}
        {leaving !== null ? (
          <div
            className="fixed inset-0 z-50 grid place-items-center bg-black/30 p-4"
            role="alertdialog"
            aria-modal="true"
            aria-label="Unsent changes"
          >
            <div className="w-full max-w-sm rounded-xl bg-paper p-6 shadow-xl">
              <h2 className="text-base font-semibold">You have changes that are not uploaded</h2>
              <p className="mt-2 text-slate">
                {leaving} change{leaving === 1 ? '' : 's'} on this computer{' '}
                {leaving === 1 ? 'has' : 'have'} not reached the server yet. If you sign out now,{' '}
                {leaving === 1 ? 'it' : 'they'} will be lost.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <Button onClick={() => setLeaving(null)}>Stay signed in</Button>
                <Button tone="danger" onClick={() => void signOut(true)}>
                  Sign out and lose them
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </NotificationsProvider>
  );
}

function ago(iso: string | null): string {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  return `${Math.round(s / 3600)} h ago`;
}

/** A sync that ends within a second is not shown, so the sidebar does not flicker while typing. */
function useCalmStatus(live: ReturnType<typeof useSyncStatus>) {
  const [shown, setShown] = useState(live);
  useEffect(() => {
    if (live.state !== 'syncing') {
      setShown(live);
      return;
    }
    const t = setTimeout(() => setShown(live), 1200);
    // Keep showing the counts and times, but not the state flip.
    setShown((s) => ({ ...live, state: s.state === 'syncing' ? 'syncing' : s.state }));
    return () => clearTimeout(t);
  }, [live]);
  return shown;
}

function SyncIndicator({ compact = false }: { compact?: boolean }) {
  const ws = useWorkspace();
  const st = useCalmStatus(useSyncStatus(ws.engine));
  const tone =
    st.state === 'error'
      ? 'bg-flag'
      : st.state === 'offline'
        ? 'bg-warn'
        : st.state === 'syncing'
          ? 'bg-brand'
          : st.pending > 0
            ? 'bg-warn'
            : 'bg-ok';
  const title =
    st.state === 'syncing'
      ? 'Syncing…'
      : st.state === 'offline'
        ? 'Offline'
        : st.state === 'error'
          ? 'Sync problem'
          : st.pending > 0
            ? 'Waiting to sync'
            : 'All changes saved';
  const detail =
    st.state === 'offline'
      ? st.pending > 0
        ? `${st.pending} change${st.pending === 1 ? '' : 's'} waiting for internet`
        : 'Working without internet'
      : st.state === 'error'
        ? (st.error ?? 'Will retry')
        : st.lastSyncedAt
          ? `Synced ${ago(st.lastSyncedAt)}`
          : '';
  if (compact) {
    return (
      <div
        className="grid h-5 place-items-center"
        role="status"
        aria-live="polite"
        aria-label={`${title}. ${detail}`}
        title={`${title}${detail ? ` · ${detail}` : ''}`}
      >
        <span
          className={clsx(
            'h-2.5 w-2.5 rounded-full',
            tone,
            st.state === 'syncing' && 'animate-pulse',
          )}
        />
      </div>
    );
  }
  return (
    <div
      className="flex items-start gap-2 text-[12px] leading-tight text-slate"
      role="status"
      aria-live="polite"
    >
      <span
        className={clsx(
          'mt-1 h-2 w-2 shrink-0 rounded-full',
          tone,
          st.state === 'syncing' && 'animate-pulse',
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="block font-medium text-ink">{title}</span>
        <span className="block truncate" title={detail}>
          {detail}
        </span>
        {st.state === 'error' || st.state === 'offline' ? (
          <button
            type="button"
            onClick={() => void ws.engine.syncNow()}
            className="mt-0.5 text-brand hover:underline"
          >
            Try now
          </button>
        ) : null}
      </span>
    </div>
  );
}

interface Conflict {
  id: string;
  table_name: string;
  reason: string;
  at: string;
  local_row: Record<string, unknown>;
  server_row: Record<string, unknown>;
}

function ConflictBanner() {
  const ws = useWorkspace();
  const res = useLiveQuery<Conflict>(
    'select id, table_name, reason, at, local_row, server_row from sync_conflicts where dismissed_at is null order by at desc',
    [],
  );
  const [open, setOpen] = useState(false);
  // A signed copy withdrawn because its signature did not go through is explained by the
  // report's own entry; listing it as well would read as a second, separate problem.
  const rows = (res?.rows ?? []).filter((c) => !c.reason.startsWith('signature_not_applied'));
  if (rows.length === 0) return null;

  const label = (c: Conflict) =>
    String(c.local_row.name ?? c.local_row.lab_no ?? c.local_row.display_name ?? c.table_name);
  const why = (c: Conflict) =>
    c.reason === 'newer_on_server'
      ? 'Someone else changed it first, so their version was kept.'
      : c.reason.replace(/^[a-z_]+: /, '');

  return (
    <div
      className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-[13px] text-warn md:px-6"
      role="status"
    >
      <div className="flex items-center justify-between gap-3">
        <span>
          {rows.length} change{rows.length === 1 ? ' was' : 's were'} not kept.{' '}
          <button
            type="button"
            className="font-medium underline"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? 'Hide details' : 'See what happened'}
          </button>
        </span>
        <button
          type="button"
          className="font-medium underline"
          onClick={async () => {
            await ws.db.query(
              'update sync_conflicts set dismissed_at = now() where dismissed_at is null',
            );
            await ws.engine.refreshCounts();
          }}
        >
          Dismiss all
        </button>
      </div>
      {open ? (
        <ul className="mt-2 grid gap-1.5 text-ink">
          {rows.map((c) => (
            <li key={c.id} className="rounded-md bg-paper px-3 py-2">
              <span className="font-medium">{label(c)}</span>{' '}
              <span className="text-slate">({c.table_name.replace(/_/g, ' ')})</span>
              <div className="text-slate">{why(c)}</div>
              <details className="mt-1 text-[12px]">
                <summary className="cursor-pointer text-brand">What you had</summary>
                <pre className="mt-1 max-h-40 overflow-auto rounded bg-canvas p-2 font-mono text-[11px]">
                  {JSON.stringify(c.local_row, null, 2)}
                </pre>
              </details>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * Tells you, once, at the top of the screen, that reports are due. It shows only for what has not
 * been read, and only until you open the notifications or close it.
 */
function DueBanner() {
  const n = useNotifications();
  // A phone's few lines are for the report being typed into.
  const editing = useInReportEditor();
  const [closed, setClosed] = useState<string | null>(null);
  const urgent = n.messages.filter((m) => m.kind === 'urgent' && !n.isRead(m.key));
  const due = n.reminders.filter((r) => !n.isRead(r.key));
  const stamp = `${due.length}:${urgent.length}:${n.today}`;
  if ((due.length === 0 && urgent.length === 0) || closed === stamp) return null;
  return (
    <div
      role="status"
      className={clsx(
        'flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-accent/25 bg-accent-soft px-4 py-2 text-[13px] text-ink md:px-6',
        editing && 'max-md:hidden',
      )}
    >
      <Bell size={14} className="text-accent" />
      <span className="font-medium">
        {urgent.length ? `${urgent[0]?.title}` : ''}
        {urgent.length && due.length ? ' · ' : ''}
        {due.length
          ? `${due.length} report${due.length === 1 ? ' is' : 's are'} due today or late`
          : ''}
      </span>
      <Link to="/notifications" className="font-semibold text-brand underline">
        Review
      </Link>
      <button
        type="button"
        className="ml-auto text-slate hover:text-ink"
        onClick={() => setClosed(stamp)}
        aria-label="Hide this notice"
      >
        Hide
      </button>
    </div>
  );
}

/** The top bar on a phone: menu, which lab, and the bell. */
function MobileBar({ onMenu }: { onMenu: () => void }) {
  const ws = useWorkspace();
  const notes = useNotifications();
  return (
    <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-paper px-2 md:hidden">
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open the menu"
        className="grid h-10 w-10 place-items-center rounded-lg text-navy active:bg-canvas"
      >
        <Menu size={22} />
      </button>
      <img src="/labview-mark.svg" alt="" className="h-7 w-7" />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate text-[14px] font-semibold text-navy">{ws.auth.orgName}</div>
        <div className="text-[10px] text-slate">LabView</div>
      </div>
      <SyncIndicator compact />
      <Link
        to="/notifications"
        aria-label={notes.unread > 0 ? `Notifications, ${notes.unread} unread` : 'Notifications'}
        className="relative grid h-10 w-10 place-items-center rounded-lg text-navy active:bg-canvas"
      >
        <Bell size={21} />
        <CountBadge n={notes.unread} compact />
      </Link>
    </header>
  );
}

/** The four places used all day, and a big "New report" button, at the thumb on a phone. */
function MobileTabs({ onMenu }: { onMenu: () => void }) {
  const ws = useWorkspace();
  const first = ws.can('view_dashboard')
    ? { to: '/dashboard', label: 'Home', icon: LayoutDashboard }
    : { to: '/notifications', label: 'Alerts', icon: Bell };
  const tab =
    'relative flex h-full flex-1 flex-col items-center justify-center gap-0.5 text-[11px] text-slate';
  const active = { className: '!text-navy font-semibold' };
  return (
    <nav
      aria-label="Quick menu"
      className="fixed inset-x-0 bottom-0 z-30 flex h-[calc(3.75rem+env(safe-area-inset-bottom))] items-stretch border-t border-line bg-paper pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <Link to={first.to} className={tab} activeProps={active}>
        <first.icon size={21} strokeWidth={1.8} />
        {first.label}
      </Link>
      <Link to="/reports" className={tab} activeProps={active} activeOptions={{ exact: true }}>
        <FileText size={21} strokeWidth={1.8} />
        Reports
      </Link>
      <Link
        to="/reports/new"
        aria-label="New report"
        className="relative flex flex-1 items-start justify-center"
      >
        <span className="-mt-4 grid h-14 w-14 place-items-center rounded-full bg-navy text-white shadow-lg shadow-navy/30 active:bg-navy-hover">
          <Plus size={26} />
        </span>
      </Link>
      <Link to="/patients" className={tab} activeProps={active}>
        <UserRound size={21} strokeWidth={1.8} />
        Patients
      </Link>
      <button type="button" onClick={onMenu} className={tab}>
        <Menu size={21} strokeWidth={1.8} />
        More
      </button>
    </nav>
  );
}

/** The full menu sliding in from the left on a phone. */
function Drawer({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const path = useRouterState({ select: (r) => r.location.pathname });
  // Going somewhere closes it (not the moment it opens).
  const opened = useRef(path);
  // biome-ignore lint/correctness/useExhaustiveDependencies: closes whenever the page changes
  useEffect(() => {
    if (path !== opened.current) onClose();
  }, [path]);
  useEffect(() => {
    const on = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', on);
    return () => window.removeEventListener('keydown', on);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Menu">
      <button
        type="button"
        aria-label="Close the menu"
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <div className="absolute inset-y-0 left-0 flex w-[85%] max-w-xs flex-col overflow-y-auto bg-paper shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the menu"
          className="absolute top-2 right-2 z-10 grid h-9 w-9 place-items-center rounded-lg text-slate active:bg-canvas"
        >
          <X size={20} />
        </button>
        {children}
      </div>
    </div>
  );
}
